import { create } from "zustand";
import * as sizes from "@picklebet/design-tokens/styles/sizes.module.scss";
import type { PageContext } from "gatsby/createPages";

type RouteDock = NonNullable<PageContext["dock"]>;

type DockContent = Exclude<RouteDock, "none">;

type Captcha = {
  required: boolean;
  visible: boolean;
};

type BiometryType = "Biometrics" | "FaceID" | "TouchID";

type BiometricAvailability = {
  available: boolean;
  enrolled: boolean;
  biometryType: BiometryType | null;
};

type ShellStore = {
  menuOpen: boolean;
  dockOpen: boolean;
  dockContentOverride: DockContent | null;
  keyboardOpen: boolean;
  modal: string | null;
  captcha: Captcha;
  isDeeplinkOverlayActive: boolean;
  windowSize: { width: number; height: number };
  deviceBiometricAvailability: BiometricAvailability;
  isBiometricProcessing: boolean;
};

type ShellActions = {
  toggleMenu: () => void;
  setMenuOpen: (open: boolean) => void;
  openDock: (content: DockContent, routeDock: RouteDock) => void;
  toggleDock: (content: DockContent, routeDock: RouteDock) => void;
  revealDock: (content: DockContent, routeDock: RouteDock) => void;
  closeDock: () => void;
  closeOverlays: () => void;
  clearDockContentOverride: () => void;
  reset: () => void;
  setKeyboardOpen: (open: boolean) => void;
  setModal: (modal: string | null) => void;
  setCaptchaPuzzle: (captcha: Captcha) => void;
  closeCaptchaPuzzle: () => void;
  setWindowSize: (size: { width: number; height: number }) => void;
  setBiometricAvailability: (state: Partial<BiometricAvailability>) => void;
  setIsBiometricProcessing: (loading: boolean) => void;
  setIsDeeplinkOverlayActive: (value: boolean) => void;
};

type ShellState = ShellStore & ShellActions;

const railQuery = `(min-width: ${sizes.widthTablet})`;

const dockedQuery = `(min-width: ${sizes.widthDesktopXl})`;

const initialShellState = {
  menuOpen: false,
  dockOpen: false,
  dockContentOverride: null,
  keyboardOpen: false,
} satisfies Partial<ShellStore>;

const resolveDockContent = (
  override: DockContent | null,
  routeDock: RouteDock,
): RouteDock => (routeDock === "none" ? "none" : override ?? routeDock);

const selectOverlayOpen = (state: ShellStore) =>
  state.menuOpen || state.dockOpen;

const useShellStore = create<ShellState>()((set, get) => ({
  ...initialShellState,
  modal: null,
  captcha: { required: false, visible: false },
  isDeeplinkOverlayActive: false,
  windowSize: { height: 0, width: 0 },
  deviceBiometricAvailability: {
    available: false,
    enrolled: false,
    biometryType: null,
  },
  isBiometricProcessing: false,
  toggleMenu: () => get().setMenuOpen(!get().menuOpen),
  setMenuOpen: (open) =>
    set(open ? { menuOpen: true, dockOpen: false } : { menuOpen: false }),
  openDock: (content, routeDock) => {
    if (routeDock === "none") return;
    set({ dockOpen: true, menuOpen: false, dockContentOverride: content });
  },
  toggleDock: (content, routeDock) => {
    const { dockOpen, dockContentOverride, openDock } = get();
    if (
      dockOpen &&
      resolveDockContent(dockContentOverride, routeDock) === content
    ) {
      set({ dockOpen: false });
      return;
    }
    openDock(content, routeDock);
  },
  revealDock: (content, routeDock) => {
    if (!window.matchMedia(railQuery).matches) return;
    get().openDock(content, routeDock);
  },
  closeDock: () => set({ dockOpen: false }),
  closeOverlays: () => set({ menuOpen: false, dockOpen: false }),
  clearDockContentOverride: () => set({ dockContentOverride: null }),
  reset: () => set({ ...initialShellState, keyboardOpen: get().keyboardOpen }),
  setKeyboardOpen: (keyboardOpen) => set({ keyboardOpen }),
  setModal: (modal) => set({ modal }),
  setCaptchaPuzzle: ({ required, visible }) =>
    set({ captcha: { required, visible } }),
  closeCaptchaPuzzle: () =>
    set({ captcha: { required: false, visible: false } }),
  setWindowSize: (windowSize) => set({ windowSize }),
  setIsDeeplinkOverlayActive: (isDeeplinkOverlayActive) =>
    set({ isDeeplinkOverlayActive }),
  setBiometricAvailability: (status) =>
    set((state) => ({
      deviceBiometricAvailability: {
        ...state.deviceBiometricAvailability,
        ...status,
      },
    })),
  setIsBiometricProcessing: (isBiometricProcessing) =>
    set({ isBiometricProcessing }),
}));

export {
  dockedQuery,
  railQuery,
  resolveDockContent,
  selectOverlayOpen,
  useShellStore,
};
export type { DockContent, RouteDock };
